import { createContext, useRef, useMemo } from "react";
import {  useSelector } from "react-redux";

import authSelector from '@Redux/auth/authSelector';

import useContextState from "@/hooks/useContextState";

import { postData, getData } from '@/utils/api';
import { transformDataToUpload, transformFetchedData } from '@/utils/transformData';
import { transformMediaType } from '@/utils/basics';
import { getItemApiPath } from '@/utils/mediaTypes';

import initialData from "./initialData";
import useIbmS3Uploader from "@/hooks/useIbmS3UploaderNew";
import { getSetting } from "@/utils/settings";
import { transformDataToUploadV2 } from "../../utils/transformData/toFetch";
import { isObject } from "../../utils/basics";
import { transformMultilanguageFiles } from "../../utils/transformData/toFetch/functions";
import { postIngest } from "../../utils/api";

export const MediaContext = createContext({
	countryCode: 'en_GB'
});

const initialStateData = JSON.parse(JSON.stringify(initialData));

export function MediaContextProvider({ children }) {
  const dataAndFunctions = useContextState({initialData, initialStateData});
  const { data, setData, setProcess } = dataAndFunctions;
  const {jwttoken: auth} = useSelector(authSelector);
  const metadataFile = useRef(null);

  // S3 uploader for metadata JSON files
  const metadataUploader =
    useIbmS3Uploader({
      setFile: () => {}, // No file state tracking needed for metadata
      removeFile: () => {}, // No remove functionality needed
      bucketPrefix: data.epId?.data ? `${data.epId.data}/` : '',
      bucketName: getSetting('ibmS3TempBucket'),
      cb: uploadMetadataCallback
    });

  const setEpId = (epId) => {
    setData((currentData) => {
      return {
        ...currentData,
        epId: {
          ...currentData.epId,
          ...epId
        }
      };
    });
  };

  const setEpIdValue = (value) => {
    setEpId({
      data: value
    });
  };

  const getEpId = async ({data, type}) => {
    try {
      setEpId({
				loading: true,
				error: null,
			});

      const response = await postData({auth, data, endpoint: `/${type}/epId`});

      if (response.error) {
        throw(new Error(response.message));
      }

      setEpId({
				loading: false,
				error: null,
				data: response.content.optionsLists[0].id,
			});

    } catch (err) {
      setEpId({
				loading: false,
				error: err.message || 'Something went wrong',
			});
    }
  };

  const upload = async (isStep = false) => {
    setProcess({
      loading: true,
      error: null,
    });

		const transformedData = transformDataToUpload(data);
		const fetchData = async () => {
			try {
				const response = await postData({auth, data: transformedData, endpoint: `/${transformMediaType(data.mediaType)}`});

				if (response.error) {
					throw(new Error(response.message));
				}

				setProcess({
          loading: false,
					error: null,
					...(isStep ? {succeed: true} : {}),
        });

        return true;
			} catch (err) {
				setProcess({
          loading: false,
					error: err.message || 'Something went wrong',
        });

        return false;
			}
		};

		return await fetchData();
	};

  const uploadIngest = async (isStep = false) => {
    setProcess({
      loading: true,
      error: null,
    });

		const transformedData = transformDataToUploadV2(data);
		const fetchData = async () => {
			try {
				const response = await postIngest({auth, data: transformedData, endpoint: `/${transformMediaType(data.mediaType)}s`});

				if (response.error) {
					throw(new Error(response.message));
				}

				setProcess({
          loading: false,
					error: null,
					...(isStep ? {succeed: true} : {}),
        });

        return true;
			} catch (err) {
				setProcess({
          loading: false,
					error: err.message || 'Something went wrong',
        });

        return false;
			}
		};

		return await fetchData();
	};

  const getItemData = async ({epId, type}) => {
    setProcess({
      loading: true,
      error: null,
    });

		const fetchData = async () => {
			try {
				const apiType = getItemApiPath(type);
				const response = await getData({auth, endpoint: `/${apiType}/${epId}`});

				if (response.error) {
					throw(new Error(response.message));
				}

				setData((currentData) =>{
          return {
            ...currentData,
            ...transformFetchedData(response.content, apiType === 'streamings'),
            process: {
              edit: true,
              loading: false,
              error: null,
              succeed: false
            }
          };
        });

        setProcess({
          loading: false,
					error: null,
        });

			} catch (err) {
				setProcess({
          loading: false,
					error: err.message || 'Something went wrong',
        });
			}
		};

		return await fetchData();
	};

  const uploadMetadata = async () => {
    setProcess({
      loading: true,
      error: null,
    });

    try {
      const transformedData = transformDataToUploadV2(data);
      const epId = data.epId.data || data.epId;
      const filename = `${epId}.json`;

      // Convert JSON object to File
      const jsonString = JSON.stringify(transformedData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const file = new File([blob], filename, { type: 'application/json' });
      metadataFile.current = {
        filename: file.name,
        filesize: file.size
      };

      metadataUploader.handleUpload({ file });
    } catch (err) {
      setProcess({
        loading: false,
        error: err.message || 'Something went wrong',
      });
    }
  };

  function uploadMetadataCallback ({error, message, key}) {
    if (error) {
      return setProcess({
        loading: false,
        error: message || 'Failed to upload metadata',
      });
    }

    if (key) {
      metadataFile.current.s3key = "/" + key;
    }

    ingestMedia();
  }

  const ingestMedia = async () => {
    let files = data.files;

    if (
      files.length === 0 &&
      isObject(data.multilanguageFiles) &&
      Object.keys(data.multilanguageFiles).length !== 0
    ) {
      files =  transformMultilanguageFiles(data.multilanguageFiles);
    }

    const allFiles = [
      ...files,
      ...(data.subtitles || []),
      ...(data.thumbnail || []),
    ];

    const filesToSend = allFiles.filter(file => file.s3key).map(file => ({
      filename: file.name,
      filesize: file.filesize,
      s3key: "/" + file.s3key,
    }));

    try {
      const dataTosend = {
        ...(data.mediaType !== 'EPV_PHOTO' ? {epId: data.epId.data || data.epId} : {}),
        ingestContent: [
          ...(data.mediaType !== 'EPV_PHOTO' &&  metadataFile.current ? [metadataFile.current] : []),
          ...filesToSend,
        ]
      };


      const response = await postData({auth, data: dataTosend, endpoint: `/ingest`, noResponse: true});

      if (response.error) {
        throw(new Error(response.message));
      }

      setProcess({
        loading: false,
        error: null,
        succeed: true,
      });
    } catch (err) {
      setProcess({
        loading: false,
        error: err.message || 'Something went wrong',
      });
    }
  };

  const value = useMemo(() => ({
    ...dataAndFunctions,
    setEpIdValue,
    getEpId,
    upload,
    getItemData,
    uploadMetadata,
    ingestMedia,
    uploadIngest
  }), [dataAndFunctions, setEpIdValue, getEpId, upload, getItemData, uploadMetadata, ingestMedia, uploadIngest]);

  return (
    <MediaContext.Provider value={value}>
      {children}
    </MediaContext.Provider>
  );
}